import { useLayoutEffect } from 'react';

// Walks up from a deal table to its section's sticky header bar (the dark
// "03 Live Deals" bar), identified by data-section-header on the shared
// Section card. Works for both the main table (header bar is the card's first
// child) and the nested "dismissed" sub-table in the Recently sections (the
// card is a grandparent), since both should pin under the same section bar.
function findSectionHeaderBar(table: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = table.parentElement;
  while (node) {
    const header = node.querySelector<HTMLElement>(':scope > [data-section-header]');
    if (header) return header;
    node = node.parentElement;
  }
  return null;
}

/**
 * Measures the live heights of the section header bar (h1) and the table's
 * column-header row (h2) and writes them as CSS custom properties on the table,
 * so the sticky column header and group bands pin at the correct offsets.
 *
 * The CSS (`DealTable.module.css`) consumes `--ember-sticky-h1/h2` with px
 * fallbacks, so this only needs to *correct* those offsets when the real
 * heights differ — e.g. the "EST. EI INV." column header wrapping to two lines,
 * browser zoom, or a late web-font swap. A ResizeObserver keeps them in sync.
 *
 * Vars are set on the <table> (not a shared ancestor) so each table — including
 * the nested dismissed sub-table — gets its own correct h2 without racing.
 */
export function useStickyHeaderOffsets(
  tableRef: React.RefObject<HTMLTableElement | null>,
) {
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    const thead = table.querySelector<HTMLElement>('thead');
    const headerBar = findSectionHeaderBar(table);

    const apply = () => {
      if (headerBar) {
        table.style.setProperty('--ember-sticky-h1', `${headerBar.offsetHeight}px`);
      }
      if (thead) {
        table.style.setProperty('--ember-sticky-h2', `${thead.offsetHeight}px`);
      }
    };
    apply();

    if (typeof ResizeObserver === 'undefined') return; // jsdom / very old browsers
    const ro = new ResizeObserver(apply);
    if (thead) ro.observe(thead);
    if (headerBar) ro.observe(headerBar);
    return () => ro.disconnect();
  }, [tableRef]);
}
